function rccFlairId(value) {
  return String(value || '').split('/').pop();
}

function rccFlairCents(money) {
  return money && money.amount != null ? Math.round(Number(money.amount) * 100) : null;
}

function rccFlairVariant(variant) {
  const compareAtPrice = rccFlairCents(variant.compareAtPrice);
  const price = rccFlairCents(variant.price);
  const saleAmount = compareAtPrice && price && compareAtPrice > price ? compareAtPrice - price : 0;

  return {
    id: rccFlairId(variant.id),
    available_for_sale: variant.availableForSale,
    barcode: variant.barcode,
    compare_at_price_cents: compareAtPrice,
    metafields: [],
    price_cents: price,
    quantity_available: variant.quantityAvailable,
    sale_amount_cents: saleAmount,
    sale_percent: compareAtPrice && saleAmount ? (saleAmount / compareAtPrice) * 100 : 0,
    selected_options: variant.selectedOptions || [],
    sku: variant.sku,
  };
}

function rccFlairProduct(product) {
  const graphql = product.graphql || {};
  const variants = (graphql.flairVariants?.nodes || []).map(rccFlairVariant);
  const availableVariants = variants.filter((variant) => variant.available_for_sale);
  const prices = variants.map((variant) => variant.price_cents).filter(Number.isFinite);
  const saleAmounts = variants.map((variant) => variant.sale_amount_cents).filter(Number.isFinite);
  const salePercents = variants.map((variant) => variant.sale_percent).filter(Number.isFinite);

  return {
    id: rccFlairId(graphql.id || product.productId),
    handle: graphql.handle || product.handle,
    title: graphql.title || product.title,
    collections: (graphql.flairCollections?.nodes || []).map((collection) => ({
      id: rccFlairId(collection.id),
    })),
    created_at: graphql.flairCreatedAt ? new Date(graphql.flairCreatedAt) : null,
    metafields: [],
    product_type: graphql.flairProductType,
    published_at: graphql.flairPublishedAt ? new Date(graphql.flairPublishedAt) : null,
    selected_variant_id: rccFlairId(product.variantId),
    tags: (graphql.tags || product.tags || []).map((tag) => String(tag).toLowerCase()),
    vendor: graphql.flairVendor || product.brand,
    variants,
    available_for_sale: availableVariants.length > 0,
    option_names: [...new Set(variants.flatMap((variant) => variant.selected_options.map((option) => option.name)))],
    option_values: [...new Set(variants.flatMap((variant) => variant.selected_options.map((option) => option.value)))],
    price_cents_max: prices.length ? Math.max(...prices) : 0,
    price_cents_min: prices.length ? Math.min(...prices) : 0,
    quantity_available: availableVariants.reduce(
      (total, variant) => total + Math.max(Number(variant.quantity_available) || 0, 0),
      0
    ),
    sale_amount_cents_max: saleAmounts.length ? Math.max(...saleAmounts) : 0,
    sale_amount_cents_min: saleAmounts.length ? Math.min(...saleAmounts) : 0,
    sale_percent_max: salePercents.length ? Math.max(...salePercents) : 0,
    sale_percent_min: salePercents.length ? Math.min(...salePercents) : 0,
  };
}

if (!customElements.get('rcc-product-badges')) {
  customElements.define(
    'rcc-product-badges',
    class RccProductBadges extends HTMLElement {
      static get graphqlRequirements() {
        return {
          graphql: [
            `
            flairCreatedAt: createdAt
            flairPublishedAt: publishedAt
            flairProductType: productType
            flairVendor: vendor
            flairCollections: collections(first: 250) { nodes { id } }
            flairVariants: variants(first: 250) {
              nodes {
                id
                availableForSale
                barcode
                compareAtPrice { amount currencyCode }
                price { amount currencyCode }
                quantityAvailable
                selectedOptions { name value }
                sku
              }
            }
          `,
          ],
        };
      }

      set product(product) {
        this._product = product;
        this.render();
      }

      async render() {
        if (!this._product) return;
        const renderId = (this._renderId || 0) + 1;
        this._renderId = renderId;

        await customElements.whenDefined('flair-badges');
        if (renderId !== this._renderId || !this.isConnected) return;

        const flairProduct = rccFlairProduct(this._product);
        const badges = document.createElement('flair-badges');
        badges.setAttribute('product-id', flairProduct.id);
        badges.setAttribute('product-handle', flairProduct.handle || '');
        if (flairProduct.selected_variant_id) {
          badges.setAttribute('variant-id', flairProduct.selected_variant_id);
        }
        badges.getProduct = async () => flairProduct;
        this.replaceChildren(badges);
      }
    }
  );
}
